import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import {
  PaziaFormButton,
  PaziaInput,
  PaziaCheckbox,
  PaziaTextarea,
  PaziaFileDropzone,
  PaziaMultiSelect,
  PaziaSelect,
  PaziaDatepicker,
  PaziaButton,
} from "components/atoms";
import { BlogPostApi, BlogPostFormValues, useBlogCategories, useAuthors, AuthorApi } from "api/shop";
import { useShopImageUpload } from "pages/Shop/Sections/SectionForms/useShopImageUpload";
import { AuthorFormModal } from "../AuthorFormModal";

interface BlogPostFormProps {
  onSubmit: (data: BlogPostFormValues) => void;
  blogPost?: BlogPostApi;
  isLoading?: boolean;
}

export const generateSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[ąàáâãäå]/g, "a")
    .replace(/[ćčç]/g, "c")
    .replace(/[ęèéêë]/g, "e")
    .replace(/[ìíîï]/g, "i")
    .replace(/[łľ]/g, "l")
    .replace(/[ńñň]/g, "n")
    .replace(/[óòôõö]/g, "o")
    .replace(/[śšş]/g, "s")
    .replace(/[ùúûü]/g, "u")
    .replace(/[ýÿ]/g, "y")
    .replace(/[żźž]/g, "z")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
};

export const BlogPostForm = ({ onSubmit, blogPost, isLoading }: BlogPostFormProps) => {
  const { uploadImage } = useShopImageUpload();
  const { categories, isLoading: categoriesLoading } = useBlogCategories();
  const categoryOptions = categories.map((category) => ({ value: category.id, label: category.name }));
  const { authors, isLoading: authorsLoading, invalidate: invalidateAuthors } = useAuthors();
  const authorOptions = authors.map((a) => ({ value: a.id, label: `${a.firstName} ${a.lastName}` }));
  const [isCreateAuthorModalOpen, setIsCreateAuthorModalOpen] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.TITLE]: blogPost?.title ?? "",
      [FORM_NAMES.SLUG]: blogPost?.slug ?? "",
      [FORM_NAMES.IS_PUBLISHED]: blogPost?.isPublished ?? false,
      [FORM_NAMES.PUBLISHED_DATE]: blogPost?.publishedDate ?? null,
      [FORM_NAMES.AUTHOR_ID]: blogPost?.author?.id ?? null,
      [FORM_NAMES.COVER_IMAGE]: blogPost?.coverImage ?? "",
      [FORM_NAMES.COVER_IMAGE_ALT]: blogPost?.coverImageAlt ?? "",
      [FORM_NAMES.EXCERPT]: blogPost?.excerpt ?? "",
      [FORM_NAMES.SHOW_TABLE_OF_CONTENTS]: blogPost?.showTableOfContents ?? false,
      [FORM_NAMES.CATEGORY_IDS]: blogPost?.categories?.map((category) => category.id) ?? [],
      [FORM_NAMES.META_TITLE]: blogPost?.metaTitle ?? "",
      [FORM_NAMES.META_DESCRIPTION]: blogPost?.metaDescription ?? "",
      [FORM_NAMES.OG_IMAGE]: blogPost?.ogImage ?? "",
      [FORM_NAMES.OG_IMAGE_ALT]: blogPost?.ogImageAlt ?? "",
    },
  });
  const { handleSubmit, watch, setValue, getValues } = methods;

  // Auto-generate slug from title if slug is empty
  const title = watch(FORM_NAMES.TITLE);
  const currentSlug = watch(FORM_NAMES.SLUG);

  const handleTitleBlur = () => {
    if (!currentSlug && title) {
      setValue(FORM_NAMES.SLUG, generateSlug(title));
    }
  };

  const handleIsPublishedChange = (checked: boolean) => {
    if (checked && !getValues(FORM_NAMES.PUBLISHED_DATE)) {
      setValue(FORM_NAMES.PUBLISHED_DATE, new Date());
    }
  };

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data as BlogPostFormValues);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid max-w-xl gap-4">
        <PaziaInput name={FORM_NAMES.TITLE} label="Tytuł wpisu" required onBlur={handleTitleBlur} />
        <PaziaInput name={FORM_NAMES.SLUG} label="Slug (URL)" required placeholder="np. moj-pierwszy-wpis" />
        <PaziaCheckbox name={FORM_NAMES.IS_PUBLISHED} label="Opublikowany" onChange={handleIsPublishedChange} />
        <PaziaDatepicker name={FORM_NAMES.PUBLISHED_DATE} label="Data publikacji" />
        <div className="flex items-end gap-2">
          <PaziaSelect
            name={FORM_NAMES.AUTHOR_ID}
            label="Autor"
            className="grow"
            options={authorOptions}
            isLoading={authorsLoading}
          />
          <PaziaButton
            type="secondary"
            btnType="button"
            title="+ Nowy autor"
            onClick={() => setIsCreateAuthorModalOpen(true)}
          />
        </div>
        <AuthorFormModal
          isOpen={isCreateAuthorModalOpen}
          onCloseRequest={() => setIsCreateAuthorModalOpen(false)}
          onSuccess={(newAuthor: AuthorApi) => {
            invalidateAuthors();
            setValue(FORM_NAMES.AUTHOR_ID, newAuthor.id);
            setIsCreateAuthorModalOpen(false);
          }}
        />
        <div className="[&_img]:max-h-48">
          <PaziaFileDropzone
            name={FORM_NAMES.COVER_IMAGE}
            label="Obraz wyróżniający"
            type="image"
            single
            preview="image"
            onDrop={async (file: File) => {
              const url = await uploadImage(file as File);
              setValue(FORM_NAMES.COVER_IMAGE, url);
            }}
          />
        </div>
        <PaziaInput name={FORM_NAMES.COVER_IMAGE_ALT} label="Obraz wyróżniający - alt (opis obrazka)" />
        <PaziaTextarea name={FORM_NAMES.EXCERPT} label="Zajawka (krótki opis)" />
        <PaziaCheckbox name={FORM_NAMES.SHOW_TABLE_OF_CONTENTS} label="Pokaż spis treści" />
        <PaziaMultiSelect
          name={FORM_NAMES.CATEGORY_IDS}
          label="Kategorie"
          options={categoryOptions}
          isLoading={categoriesLoading}
        />
        <p className="mt-2 text-xs font-semibold text-slate-600 dark:text-slate-400">SEO</p>
        <PaziaInput name={FORM_NAMES.META_TITLE} label="Meta title (SEO)" />
        <PaziaTextarea name={FORM_NAMES.META_DESCRIPTION} label="Meta description (SEO)" />
        <p className="mt-2 text-xs font-semibold text-slate-600 dark:text-slate-400">OG Image (Social Media)</p>
        <div className="[&_img]:max-h-48">
          <PaziaFileDropzone
            name={FORM_NAMES.OG_IMAGE}
            label="OG Image"
            type="image"
            single
            preview="image"
            onDrop={async (file: File) => {
              const url = await uploadImage(file as File);
              setValue(FORM_NAMES.OG_IMAGE, url);
            }}
          />
        </div>
        <PaziaInput name={FORM_NAMES.OG_IMAGE_ALT} label="OG Image Alt (opis obrazka)" />
        <PaziaFormButton disabled={isLoading} />
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  TITLE: "title",
  SLUG: "slug",
  IS_PUBLISHED: "isPublished",
  PUBLISHED_DATE: "publishedDate",
  AUTHOR_ID: "authorId",
  COVER_IMAGE: "coverImage",
  COVER_IMAGE_ALT: "coverImageAlt",
  EXCERPT: "excerpt",
  SHOW_TABLE_OF_CONTENTS: "showTableOfContents",
  CATEGORY_IDS: "categoryIds",
  META_TITLE: "metaTitle",
  META_DESCRIPTION: "metaDescription",
  OG_IMAGE: "ogImage",
  OG_IMAGE_ALT: "ogImageAlt",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.TITLE]: yup.string().required("Tytuł jest wymagany"),
    [FORM_NAMES.SLUG]: yup
      .string()
      .required("Slug jest wymagany")
      .matches(
        /^(\/|[a-z0-9-]+(?:\/[a-z0-9-]+)*)$/,
        "Slug może zawierać małe litery, cyfry i myślniki, np. moj-pierwszy-wpis",
      ),
    [FORM_NAMES.IS_PUBLISHED]: yup.boolean().default(false),
    [FORM_NAMES.PUBLISHED_DATE]: yup.date().nullable().optional(),
    [FORM_NAMES.AUTHOR_ID]: yup.number().nullable().optional(),
    [FORM_NAMES.COVER_IMAGE]: yup.string().optional(),
    [FORM_NAMES.COVER_IMAGE_ALT]: yup.string().optional(),
    [FORM_NAMES.EXCERPT]: yup.string().optional(),
    [FORM_NAMES.SHOW_TABLE_OF_CONTENTS]: yup.boolean().default(false),
    [FORM_NAMES.CATEGORY_IDS]: yup.array().of(yup.number().required()).default([]),
    [FORM_NAMES.META_TITLE]: yup.string().optional(),
    [FORM_NAMES.META_DESCRIPTION]: yup.string().optional(),
    [FORM_NAMES.OG_IMAGE]: yup.string().optional(),
    [FORM_NAMES.OG_IMAGE_ALT]: yup.string().optional(),
  })
  .required();

interface FormValues {
  title: string;
  slug: string;
  isPublished: boolean;
  publishedDate?: Date | string | null;
  authorId?: number | null;
  coverImage?: string;
  coverImageAlt?: string;
  excerpt?: string;
  showTableOfContents: boolean;
  categoryIds: number[];
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
}
